import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withSnackbar } from 'notistack';
import {
	setAlertActionCreator,
	setTableStateActionCreator,
} from 'plugins/store/reducers/common.reducer';
import { setNumberRowsPerPageActionCreator } from 'plugins/store/reducers/settings.reducer';
import { can } from 'utils/permissions/permissionsChecker';
import permissionsMap from 'utils/permissions/permissionsMap';
import layoutNames from 'utils/enums/layout-names.enum';

// @material
import withStyles from '@material-ui/core/styles/withStyles';

// core components
import ActionButtons from 'components/ActionButtons/ActionButtons';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CardBody from 'components/Card/CardBody';
import SnackMessage from 'components/Snackbar/SnackMessage';
import CustomPhoneLabel from 'components/CustomPhoneLabel/CustomPhoneLabel';

import ReactTable from 'components/ReactTable/ReactTable';
import DateFormatResolver, { fullDateUsFormat } from 'resolvers/date-format.resolver';

import {
	searchEventLogs,
	deleteEventLogs,
	deleteAllEventLogs,
} from 'services/marketing.service';

import { mapNotificationsResult } from 'mappings/pushNotificationsMappings';

import CheckMark from 'assets/img/Checkmark.svg';
import Close from 'assets/img/Close.svg';

import cardHeaderStyle from 'assets/jss/material-dashboard-pro-react/components/cardHeaderStyle';

const styles = {
	...cardHeaderStyle,
	wrapper: {
		'& label': {
			'@media (max-width: 1400px)': {
				fontSize: '13px',
			},
			'@media (max-width: 1279px)': {
				fontSize: '14px',
			},
		},
	},
	loadingIndicator: {
		position: 'absolute',
		top: '0',
		left: '50%',
		transform: 'translate(-50%; -50%)',
		zIndex: '10',
	},
};

class MarketingEventLogs extends Component {

	state = {
		data: [],
		totalRecordsCount: 0,
		pageCount: 0,
		loading: true,
	};

	columns = [
		{
			Header: 'Name',
			accessor: 'type',
		},
		{
			Header: 'Customer\'s phone number',
			accessor: 'customerPhoneNumber',
			Cell: ({ value }) => (value ? (<CustomPhoneLabel value={value} />) : null),
		},
		{
			Header: 'Sent at',
			accessor: 'sentAt',
			sortDescFirst: true,
			Cell: ({ value }) => {
				return DateFormatResolver.resolve(value, fullDateUsFormat)
			}
		},
		{
			Header: 'Sent',
			accessor: 'isSent',
			Cell: ({ value }) => {
				return value
					? <img src={CheckMark} alt='Sent' />
					: <img src={Close} alt='Not Sent' />
			},
			className: 'flag-align-right',
		},
	]

	fetchData = async ({
		filterQuery,
		pageIndex,
		pageSize,
		dateRangeFilter,
		sortBy,
	}) => {

		const {
			setRowsPerPage,
			setTableState,
		} = this.props;

		this.setState({ loading: true });
		const result = await searchEventLogs({
			filterQuery,
			dateRangeFilter,
			pageIndex,
			pageSize,
			sortBy,
		});

		const mappedResult = mapNotificationsResult(result);

		this.setState({
			data: mappedResult.data,
			totalRecordsCount: mappedResult.total,
			pageCount: Math.ceil(mappedResult.total / pageSize),
			loading: false,
		});

		setRowsPerPage({
			table: 'marketingEventLogNotifications',
			rowsPerPage: pageSize
		});

		setTableState({
			table: 'marketingEventLogNotifications',
			state: {
				searchQuery: filterQuery,
				sortBy: sortBy,
				dateRangeFilter,
				page: pageIndex
			}
		});
	};

	onDelete = (selection, refreshData) => {
		const {
			setAlert,
			enqueueSnackbar,
		} = this.props;

		const idsToDelete = selection.map((item) => {
			return item.original.id;
		});

		const deleteNotifications = {
			type: 'warning',
			title: 'Do you want to remove selected Event Log(s)?',
			message: 'This action can not be undone',
			confirmBtnText: 'REMOVE',
			onConfirm: async () => {
				setAlert(null);
				try {
					await deleteEventLogs(idsToDelete);
					refreshData();
					enqueueSnackbar(
						'Event Log(s) successfully removed',
						{
							content: (key, message) => (
								<SnackMessage
									id={key}
									message={message}
									color="success"
								/>
							),
						}
					);
				} catch (e) {
					enqueueSnackbar(e.resolvedErrorMessage, {
						content: (key, message) => (
							<SnackMessage
								id={key}
								message={message}
								color="danger"
							/>
						),
					});
				}
			},
			onCancel: () => {
				setAlert(null);
			},
		};
		setAlert(deleteNotifications);
	};

	onDeleteAll = () => {
		const {
			setAlert,
			enqueueSnackbar,
		} = this.props;

		const deleteAllNotifications = {
			type: 'warning',
			title: 'Remove all Event Logs?',
			message: 'This action can not be undone',
			confirmBtnText: 'REMOVE',
			onConfirm: async () => {
				setAlert(null);
				try {
					await deleteAllEventLogs();
					const { defaultDateRangeFilter, rowsPerPage, defaultSortBy } = this.props;

					this.fetchData({
						filterQuery: '',
						dateRangeFilter: defaultDateRangeFilter,
						pageIndex: 0,
						pageSize: rowsPerPage,
						sortBy: defaultSortBy,
					});

					enqueueSnackbar(
						'Event Logs successfully removed',
						{
							content: (key, message) => (
								<SnackMessage
									id={key}
									message={message}
									color="success"
								/>
							),
						}
					);
				} catch (e) {
					enqueueSnackbar(e.resolvedErrorMessage, {
						content: (key, message) => (
							<SnackMessage
								id={key}
								message={message}
								color="danger"
							/>
						),
					});
				}
			},
			onCancel: () => {
				setAlert(null);
			},
		};
		setAlert(deleteAllNotifications);
	};

	isClearSearchAndFilters = () => {
		const { defaultDateRangeFilter, defaultSearchQuery } = this.props;

		if (
			defaultDateRangeFilter?.from !== null
			|| defaultDateRangeFilter?.to !== null
			|| defaultSearchQuery !== ''
		) {
			return false;
		}
		return true;
	}

	render() {
		const actionItems = [];
		const tooltip = this.isClearSearchAndFilters() ? '' : 'Reset filtering to remove all records';
		const disabled = !this.isClearSearchAndFilters();

		if (can(permissionsMap.marketing.items.broadcast.delete)) {
			actionItems.push({
				label: 'Remove All',
				fn: this.onDeleteAll,
				tooltip,
				disabled,
			});
		}

		const actionButtons = <ActionButtons items={actionItems}/>;

		const {
			rowsPerPage,
			defaultSearchQuery,
			defaultSortBy,
			defaultDateRangeFilter,
			defaultPageIndex,
		} = this.props;

		const {
			data,
			totalRecordsCount,
			pageCount,
			loading,
		} = this.state;

		return (
			<CardBody>
				<GridContainer>
					<GridItem xs={12} sm={12} md={12}>
						<ReactTable
							title={{
								text: 'Past Events',
								withCardIcon: false,
							}}
							columns={this.columns}
							data={data}
							fetchData={this.fetchData}
							rowsPerPage={rowsPerPage}
							defaultSearchQuery={defaultSearchQuery}
							defaultDateRangeFilter={defaultDateRangeFilter}
							defaultSortBy={defaultSortBy}
							defaultPageIndex={defaultPageIndex}
							loading={loading}
							pageCount={pageCount}
							totalRecordsCount={totalRecordsCount}
							detailsPath={`${layoutNames.portal}/marketing/details`}
							onDelete={this.onDelete}
							isPageRootComponent={false}
							addRecordComponent={actionButtons}
						>
						</ReactTable>
					</GridItem>
				</GridContainer>
			</CardBody>
		);
	}
}

const mapStateToProps = ({
	common: {
		tablesState: {
			marketingEventLogNotifications: {
				searchQuery,
				dateRangeFilter,
				sortBy,
				page,
			},
		},
	},
	settings: { rowsPerPage } }) => {
	return {
		rowsPerPage: rowsPerPage.marketingEventLogNotifications,
		defaultSearchQuery: searchQuery,
		defaultDateRangeFilter: dateRangeFilter,
		defaultSortBy: sortBy,
		defaultPageIndex: page,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(setAlertActionCreator, dispatch),
	setRowsPerPage: bindActionCreators(
		setNumberRowsPerPageActionCreator,
		dispatch
	),
	setTableState: bindActionCreators(
		setTableStateActionCreator,
		dispatch
	),
});

export default compose(
	withSnackbar,
	withStyles(styles),
	connect(mapStateToProps, mapDispatchToProps),
)(MarketingEventLogs);
